import {
  deleteRoutesFailed,
  deleteRoutesSucceeded,
  editRouteFailed,
  editRouteSucceeded,
  importActivitiesSpreadsheetFailed,
  importActivitiesSpreadsheetSucceeded,
  parseSpreadsheetFailed,
  parseSpreadsheetSucceeded,
  routingFailed,
  routingSucceeded,
} from './actionCreators';
import type { DeleteRoutesRequest, EditRouteRequest, ImportActivitiesSpreadsheetRequest, ParseSpreadsheetRequest, RoutingRequest } from './actions';
import { ActionTypes } from './actionTypes';
import { parseRoutingErrors, parserRouteEditingErrors, parseSpreadsheetErrors } from './errors';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import type { Session, UUID } from '@cobliteam/common-types';

import { GeocodeRequester, GraphQLRoutingRequester, type InputActivity, type NewRoute, type ParsedSpreadsheetTab, type WebData } from '@cobliteam/api-lib';

import { Environment } from '@cobliteam/react-environments';
import { RoutingPermissions } from '@cobliteam/routing/src/permissions';

import { selectLoginWebData } from '@cobliteam/store';

import { checkAccessAllowed } from '@cobliteam/hooks/src/usePermissions';

import Alert from '@cobliteam/ui/components/Alert';

import { getDataFromSpreadsheet } from '../../utils';

export const graphQLRoutingRequester: GraphQLRoutingRequester = new GraphQLRoutingRequester();

const env = Environment.getInstance().getEnvironment();
export const geocodeRequester: GeocodeRequester = new GeocodeRequester({
  host: env.herbieApiHost,
});

export const defaultAlertConfig = { hasCloseBtn: true };

export const spreadsheetAlertConfig = { ttl: 6000 };

export function* requestRoutingSaga(action: RoutingRequest) {
  const routingSettings = action.payload;

  try {
    const loginWebData: WebData<Session> = yield select(selectLoginWebData);
    const routes: NewRoute[] = yield call(graphQLRoutingRequester.createRoutes, routingSettings, checkAccessAllowed(loginWebData, RoutingPermissions.VIEW_PROOF_OF_CONCLUSION));
    yield put(routingSucceeded(routes));
  } catch (err) {
    Alert.error(parseRoutingErrors(err));
    yield put(routingFailed());
  }
}

export function* requestEditRouteSaga(action: EditRouteRequest) {
  try {
    const loginWebData: WebData<Session> = yield select(selectLoginWebData);

    const { editRoute, notifyRouteEditRequestResult } = action.payload;

    const editedRoute: NewRoute = yield call(graphQLRoutingRequester.editRoute, editRoute, checkAccessAllowed(loginWebData, RoutingPermissions.VIEW_PROOF_OF_CONCLUSION));

    yield put(editRouteSucceeded(editedRoute));

    if (notifyRouteEditRequestResult === true) {
      Alert.success('Rota editada com sucesso!');
    }

    if (notifyRouteEditRequestResult === false) {
      Alert.success('Rota duplicada com sucesso.');
    }
  } catch (err) {
    yield put(editRouteFailed());
    Alert.error(parserRouteEditingErrors(err));
  }
}

export function* requestDeleteRoutesSaga(action: DeleteRoutesRequest) {
  try {
    const { routesToDelete, notifyDeleteRouteRequestResult } = action.payload;
    const deletedRoutes: UUID[] = yield call(graphQLRoutingRequester.deleteRoutes, routesToDelete);
    yield put(deleteRoutesSucceeded(deletedRoutes));
    if (notifyDeleteRouteRequestResult) {
      Alert.success('Rota excluída com sucesso!');
    }
  } catch (err) {
    yield put(deleteRoutesFailed());
  }
}

export function* requestActivitiesSpreadsheetImportSaga(action: ImportActivitiesSpreadsheetRequest) {
  const formValues = action.payload;

  try {
    const spreadsheetImportResultAddresses: InputActivity[] = yield call(graphQLRoutingRequester.importSpreadsheet, formValues);
    yield put(importActivitiesSpreadsheetSucceeded(spreadsheetImportResultAddresses));
  } catch (err) {
    Alert.error('Houve um erro, tente novamente mais tarde.');
    yield put(importActivitiesSpreadsheetFailed());
  }
}

export function* parseSpreadsheetSaga(file: ParseSpreadsheetRequest) {
  try {
    const parsedSpreadsheet: ParsedSpreadsheetTab[] = yield call(getDataFromSpreadsheet, file);
    yield put(parseSpreadsheetSucceeded(parsedSpreadsheet));
  } catch (err) {
    Alert.error(parseSpreadsheetErrors(err));
    yield put(parseSpreadsheetFailed());
  }
}

export function* NewRoutingSagasListener() {
  yield all([
    takeLatest(ActionTypes.ROUTING_REQUEST, requestRoutingSaga),
    takeEvery(ActionTypes.EDIT_ROUTE_REQUEST, requestEditRouteSaga),
    takeLatest(ActionTypes.DELETE_ROUTES_REQUEST, requestDeleteRoutesSaga),
    takeLatest(ActionTypes.IMPORT_ACTIVITIES_SPREADSHEET_REQUEST, requestActivitiesSpreadsheetImportSaga),
    takeLatest(ActionTypes.PARSE_SPREADSHEET_REQUEST, parseSpreadsheetSaga),
  ]);
}
